// ── bandOutcomes — Compact outcome-band builder for GameLinesSection ──
//
// Turns the section's visible bet rows into the CompactOutcome[] the
// CompactOutcomeBands renderer consumes: one band per named outcome (#105).
//
// Bands are for outcomes with NO scale between them — the moneyline teams, the
// Tie, BTTS yes. Value LADDERS (a player+stat's 1+/2+/3+, a team's runs) are
// the opposite: their rungs differ only by a threshold, so they collapse into a
// single TABLE row with a line strip and keep every analytics column — see
// ladderRows.ts. #141 collapsed them into a band instead, which dropped Odds /
// Vol / Liq / Q / Δ from surfaces that need them (Dan, 2026-07-24); the
// identity-based rung selection and deep-link behavior it introduced were right
// and live on in ladderRows.
//
// Pure — no store, hooks or React. Extracted from GameLinesSection so band
// building and the deep-link helpers are unit-testable directly against the
// real code, the same reason lineBetRows is its own module.

import { chipKeyFor } from './ladderRows';
import type { CompactOutcome } from './CompactOutcomeBands';
import type { GameRow, TeamSide, VenueBook } from '../ValueDashboard/types';

/** A section bet row, reduced to what band-building reads. GameLinesSection's
 *  `visibleRows` are a structural superset of this. */
export interface BandSourceRow {
  /** Passes the 0-vol / settled filters — hidden rows are skipped entirely. */
  visible: boolean;
  settled: boolean;
  synth: GameRow;
  /** Single-outcome row (BTTS, the ML Tie). Undefined → an away/home pair that
   *  becomes two bands. */
  singleSide?: TeamSide;
}

/** Where a band routes its trade / expand / positions. */
export interface BandTarget {
  synth: GameRow;
  teamSide: TeamSide;
}

export interface BandBuildResult {
  /** Bands in render order — one per outcome. */
  outcomes: CompactOutcome[];
  /** outcome.key → routing target. */
  targets: Map<string, BandTarget>;
}

/** Build the Compact bands + routing map from the section's visible rows. */
export function buildBandOutcomes(rows: BandSourceRow[]): BandBuildResult {
  const bandOutcomes: CompactOutcome[] = [];
  const bandTargets = new Map<string, BandTarget>();
  for (const r of rows) {
    if (!r.visible) continue;
    const s = r.synth;
    const add = (teamSide: TeamSide, label: string) => {
      const kalshi = (teamSide === 'away' ? s.kalshi?.away : s.kalshi?.home) ?? null;
      const polymarket = (teamSide === 'away' ? s.polymarket?.away : s.polymarket?.home) ?? null;
      // No book on either venue isn't a market — skip rather than print an
      // empty band, so "unavailable" stays truthful.
      if (!kalshi && !polymarket) return;
      // Keyed on the synth key PLUS the side: sibling outcomes share a synth on
      // the away/home pair, so the side is what makes it unique. Keying on
      // anything shared would expand the wrong band.
      const key = `${s.key}|${teamSide}`;
      bandOutcomes.push({ key, label, kalshi, polymarket, settled: r.settled });
      bandTargets.set(key, { synth: s, teamSide });
    };
    if (r.singleSide) {
      add(r.singleSide, r.singleSide === 'away' ? s.awayName : s.homeName);
    } else {
      add('away', s.awayName);
      add('home', s.homeName);
    }
  }
  return { outcomes: bandOutcomes, targets: bandTargets };
}

/** True if any of a row's venue books (either side) carries this market ticker,
 *  case-insensitively (Kalshi tickers are upper-case; Poly slugs are lower). */
export function synthMatchesTicker(synth: GameRow, ticker: string): boolean {
  const ft = ticker.toUpperCase();
  const m = (b: VenueBook | null | undefined) => !!b && b.marketTicker.toUpperCase() === ft;
  return (
    m(synth.kalshi?.away) ||
    m(synth.kalshi?.home) ||
    m(synth.polymarket?.away) ||
    m(synth.polymarket?.home)
  );
}

/** Whether a Compact bet row renders under the density filters. A row is always
 *  shown when it is the deep-link focus target (`isFocus`) — so a rung a user
 *  linked to stays selectable even if Hide-0-Vol / Hide-settled would otherwise
 *  drop it. That is what keeps the collapsed row from disagreeing with the
 *  already-seeded ticket when the linked rung has no volume (#141 review). */
export function bandRowVisible(opts: {
  hasVolume: boolean;
  settled: boolean;
  hideNoVol: boolean;
  hideSettled: boolean;
  isFocus: boolean;
}): boolean {
  if (opts.isFocus) return true;
  return !(opts.hideNoVol && !opts.hasVolume) && !(opts.hideSettled && opts.settled);
}

/** Which collapsed ladder + rung a deep-link should select. */
export interface FocusRungSelection {
  groupKey: string;
  /** The rung's chip id (its market ticker) — the value to store in
   *  `rungKeyByGroup`; computed the SAME way buildLadderTableRows computes chip
   *  ids, so the selection actually resolves to this rung. */
  chipKey: string;
  venue: 'kalshi' | 'polymarket';
}

/** Deep-link support (#141): given the section's built bet rows and a focus
 *  ticker, if it resolves to a value-ladder rung, return which ladder to
 *  select, the rung's chip id to select within it, and the matched venue. Null
 *  for a non-ladder target or no match. Lets the Positions deep-link seed the
 *  collapsed row's rung so it agrees with the already-seeded trade ticket
 *  (without it the row shows its default rung while the ticket holds the linked
 *  one — the selected-market-identity divergence). */
export function resolveFocusRungSelection(
  rows: ReadonlyArray<{ synth: GameRow; ladder?: { groupKey: string } }>,
  focusTicker: string,
): FocusRungSelection | null {
  const ft = focusTicker.toUpperCase();
  const matches = (b: VenueBook | null | undefined) => !!b && b.marketTicker.toUpperCase() === ft;
  const target = rows.find((r) => synthMatchesTicker(r.synth, ft));
  if (!target || !target.ladder) return null;
  const kalshiHit = matches(target.synth.kalshi?.away) || matches(target.synth.kalshi?.home);
  const side: TeamSide = kalshiHit
    ? matches(target.synth.kalshi?.home) && !matches(target.synth.kalshi?.away)
      ? 'home'
      : 'away'
    : matches(target.synth.polymarket?.home) && !matches(target.synth.polymarket?.away)
      ? 'home'
      : 'away';
  const kBook = side === 'away' ? target.synth.kalshi?.away : target.synth.kalshi?.home;
  const pBook = side === 'away' ? target.synth.polymarket?.away : target.synth.polymarket?.home;
  return {
    groupKey: target.ladder.groupKey,
    chipKey: chipKeyFor(kBook, pBook, `${target.synth.key}|${side}`),
    venue: kalshiHit ? 'kalshi' : 'polymarket',
  };
}
